import React from "react";

export default function MediaCarousel({ lightbox, onClose, onChange }) {
  const { items, idx } = lightbox;
  const total = items.length;
  const current = items[idx];
  const touchRef = React.useRef({ x: 0, active: false });

  const goPrev = React.useCallback(() => {
    if (total < 2) return;
    onChange({ items, idx: (idx - 1 + total) % total });
  }, [items, idx, total, onChange]);

  const goNext = React.useCallback(() => {
    if (total < 2) return;
    onChange({ items, idx: (idx + 1) % total });
  }, [items, idx, total, onChange]);

  React.useEffect(() => {
    const onKey = (event) => {
      if (event.key === "ArrowLeft") goPrev();
      else if (event.key === "ArrowRight") goNext();
      else if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext, onClose]);

  React.useEffect(() => {
    if (total < 2) return;
    [1, -1].forEach((direction) => {
      const neighbor = items[(idx + direction + total) % total];
      if (neighbor && !neighbor.isVideo) {
        const image = new Image();
        image.src = neighbor.src;
      }
    });
  }, [idx, items, total]);

  const onTouchStart = (event) => {
    touchRef.current = { x: event.touches[0].clientX, active: true };
  };
  const onTouchEnd = (event) => {
    if (!touchRef.current.active) return;
    const delta = (event.changedTouches[0]?.clientX ?? touchRef.current.x) - touchRef.current.x;
    touchRef.current.active = false;
    if (Math.abs(delta) < 40) return;
    if (delta > 0) goPrev();
    else goNext();
  };

  return (
    <div
      className="media-lightbox"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="media-lightbox"
    >
      <button
        type="button"
        className="media-lightbox-close"
        onClick={(event) => { event.stopPropagation(); onClose(); }}
        aria-label="Close viewer"
        data-testid="media-lightbox-close"
      >×</button>

      {total > 1 && (
        <>
          <button
            type="button"
            className="media-lightbox-nav prev"
            onClick={(event) => { event.stopPropagation(); goPrev(); }}
            aria-label="Previous"
            data-testid="media-lightbox-prev"
          >‹</button>
          <button
            type="button"
            className="media-lightbox-nav next"
            onClick={(event) => { event.stopPropagation(); goNext(); }}
            aria-label="Next"
            data-testid="media-lightbox-next"
          >›</button>
        </>
      )}

      <div
        className="media-lightbox-content"
        onClick={(event) => event.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {current.isVideo ? (
          <video
            key={current.id}
            src={current.src}
            controls
            autoPlay
            playsInline
            data-testid="media-lightbox-video"
          />
        ) : (
          <img
            key={current.id}
            src={current.src}
            alt={current.title}
            data-testid="media-lightbox-image"
          />
        )}
        {(current.title || total > 1) && (
          <div className="media-lightbox-title">
            {current.title}
            {total > 1 && <span className="media-lightbox-counter"> · {idx + 1} / {total}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
